import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import IcalSubscribe from "@/components/IcalSubscribe";
import TodayView from "@/components/TodayView";
import { Layout, FileText, Image as ImageIcon, FormInput, ArrowUpRight, Euro, TrendingUp, CalendarDays, Sparkles } from "lucide-react";

export default function CmsDashboard() {
  const { salon, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ pages: 0, blog: 0, galleries: 0, forms: 0 });
  const [tokens, setTokens] = useState({});
  const [dash, setDash] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!salon) return;
    Promise.all([
      api.get("/cms/pages").catch(() => ({ data: [] })),
      api.get("/cms/blog").catch(() => ({ data: [] })),
      api.get("/cms/gallery").catch(() => ({ data: [] })),
      api.get("/cms/forms").catch(() => ({ data: [] })),
    ]).then(([p, b, g, f]) => {
      setStats({ pages: p.data.length, blog: b.data.length, galleries: g.data.length, forms: f.data.length });
    }).catch(() => {});
    api.get("/cms/ical-token").then(({ data }) => setTokens(data || {})).catch(() => {});
    api.get("/cms/dashboard/stats").then(({ data }) => setDash(data)).catch(() => {});
    api.get("/cms/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [salon]);

  const refreshTokens = async () => {
    const { data } = await api.get("/cms/ical-token");
    setTokens(data || {});
  };

  if (!salon) {
    return (
      <div className="p-12">
        <h1 className="font-display text-3xl">Kein Salon zugeordnet</h1>
        <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>Bitten Sie den Super-Admin, Sie einem Salon zuzuordnen.</p>
      </div>
    );
  }

  const cards = [
    { label: "Seiten", v: stats.pages, to: "/cms/pages", icon: Layout, hint: "Seiten gestalten und veröffentlichen" },
    { label: "Blogbeiträge", v: stats.blog, to: "/cms/blog", icon: FileText, hint: "Artikel schreiben und veröffentlichen" },
    { label: "Galerien", v: stats.galleries, to: "/cms/gallery", icon: ImageIcon, hint: "Vorher/Nachher und Salonfotos" },
    { label: "Formulare", v: stats.forms, to: "/cms/forms", icon: FormInput, hint: "Kontakt- und Buchungsformulare" },
  ];

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <p className="mv-label mb-2">Willkommen zurück</p>
      <h1 className="font-display text-4xl mb-2">{salon.name}</h1>
      <p className="text-sm mb-10" style={{ color: "var(--mv-text-muted)" }}>
        Ihre Website-Zentrale. Bearbeiten, gestalten, veröffentlichen.
      </p>

      {settings && !settings.onboarding_completed && user?.role === "salon_admin" && (
        <Link to="/cms/onboarding" className="mv-card p-5 mb-8 flex items-center justify-between group" style={{ background: "linear-gradient(135deg, rgba(200,122,95,0.08) 0%, rgba(139,157,131,0.08) 100%)" }} data-testid="onboarding-banner">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            <div>
              <p className="font-display text-lg">Onboarding fertigstellen</p>
              <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>5 Schritte bis zum perfekten Salon-Setup</p>
            </div>
          </div>
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
        </Link>
      )}

      <TodayView />

      {dash && user?.role === "salon_admin" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8" data-testid="revenue-stats">
          <div className="mv-card p-5">
            <div className="flex items-center justify-between mb-3"><Euro className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} /><p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Heute</p></div>
            <p className="font-display text-3xl mb-1" data-testid="today-revenue">{dash.today.total_revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{dash.today.bookings} Termine · {dash.today.confirmed_bookings} bestätigt</p>
          </div>
          <div className="mv-card p-5">
            <div className="flex items-center justify-between mb-3"><TrendingUp className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-secondary)" }} /><p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Monat (geplant)</p></div>
            <p className="font-display text-3xl mb-1" data-testid="month-expected">{dash.month.expected_revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{dash.month.bookings} geplante Termine</p>
          </div>
          <div className="mv-card p-5">
            <div className="flex items-center justify-between mb-3"><CalendarDays className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-accent)" }} /><p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Monat (realisiert)</p></div>
            <p className="font-display text-3xl mb-1">{dash.month.realized_revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{dash.month.completed_bookings} abgeschlossen</p>
          </div>
          <Link to="/cms/bookings?status=open" className="mv-card p-5 hover:shadow-md transition-shadow" data-testid="stat-awaiting-confirmation">
            <div className="flex items-center justify-between mb-3"><CalendarDays className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} /><ArrowUpRight className="w-4 h-4 opacity-50" strokeWidth={1.5} /></div>
            <p className="font-display text-3xl mb-1">{dash.awaiting_total ?? dash.today.pending_bookings}</p>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Warten auf Bestätigung</p>
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="mv-card p-5 group" data-testid={`stat-${c.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-4">
              <c.icon className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </div>
            <p className="font-display text-3xl mb-1">{c.v}</p>
            <p className="mv-label">{c.label}</p>
            <p className="text-xs mt-3 leading-relaxed" style={{ color: "var(--mv-text-muted)" }}>{c.hint}</p>
          </Link>
        ))}
      </div>

      <div className="mv-card p-8" style={{ background: "linear-gradient(135deg, #FDFBF7 0%, #F4F1EB 100%)" }}>
        <p className="mv-label mb-2">Schnellstart</p>
        <h2 className="font-display text-2xl mb-3">Bereit zum Veröffentlichen?</h2>
        <p className="text-sm mb-6 max-w-xl" style={{ color: "var(--mv-text-muted)" }}>
          Konfigurieren Sie zuerst Ihr Theme (Farben & Schrift), bauen Sie dann Ihre Startseite per Drag & Drop und veröffentlichen Sie sie auf <b>{salon.subdomain}</b>.
        </p>
        <div className="flex gap-3 flex-wrap">
          <Link to="/cms/theme" className="mv-btn">Theme einrichten</Link>
          <Link to="/cms/pages" className="mv-btn-ghost">Seiten bearbeiten</Link>
        </div>
      </div>

      {(tokens?.staff_token || tokens?.salon_token) && (
        <div className="mt-8">
          <IcalSubscribe tokens={{ ...tokens, own_staff_id: user?.staff_id }} onRegenerated={refreshTokens} />
        </div>
      )}
    </div>
  );
}
